'use client';

import React, { Component, startTransition, type ReactNode } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { logError } from '@/app/lib/logError';
import {
  isBrowserExtensionDomError,
  isDomManipulationError,
  isHydrationMismatchError,
  isLegacyBrowserSyntaxError,
  isProxiedSessionError,
  isPermissionPolicyDenial,
  syntheticError,
} from '@/app/lib/errorFilters';

interface Props {
  children: ReactNode;
  /** The current pathname. A caught error is cleared when it changes — see `componentDidUpdate`. */
  resetKey: string;
  /** Refetch the route's server data; run by the generic fallback's Try Again. */
  onRetry: () => void;
}

interface State {
  hasError: boolean;
  isBrowserExtensionError: boolean;
  retryCount: number;
}

const MAX_AUTO_RETRIES = 2;

// A rejection whose reason is a DOM error Event (img/script/link/media onerror
// = reject) is an asset-load failure, not an actionable JS error. App code
// rejects with Error/string/object — never a bare Event — so dropping plain
// error Events is safe. A genuine ErrorEvent carrying a message is kept (that's
// a real script error worth surfacing).
function isResourceLoadRejection(reason: unknown): boolean {
  if (typeof Event === 'undefined' || !(reason instanceof Event) || reason.type !== 'error') return false;
  if (typeof ErrorEvent !== 'undefined' && reason instanceof ErrorEvent && reason.message) return false;
  return true;
}

// Builds a human-readable label for non-Error rejection reasons. Event objects
// (the common case for img.onerror = reject) have non-enumerable properties so
// JSON.stringify returns `{}`; pull out the diagnostic fields explicitly.
function describeRejectionReason(reason: unknown): string {
  try {
    const ctor = (reason as { constructor?: { name?: string } } | null)?.constructor?.name;

    if (typeof Event !== 'undefined' && reason instanceof Event) {
      const parts: string[] = [`type=${reason.type}`];

      if (typeof ErrorEvent !== 'undefined' && reason instanceof ErrorEvent) {
        if (reason.message) parts.push(`message=${reason.message.slice(0, 120)}`);
        if (reason.filename) parts.push(`file=${reason.filename.slice(0, 120)}`);
        if (reason.lineno != null) parts.push(`line=${reason.lineno}`);
        if (reason.error && (reason.error as { message?: unknown }).message) {
          parts.push(`err=${String((reason.error as { message: unknown }).message).slice(0, 120)}`);
        }
      }

      const target = (reason.target ?? reason.currentTarget) as
        (HTMLElement & { src?: string; href?: string }) | null;
      if (target?.tagName) parts.push(`tag=${target.tagName.toLowerCase()}`);
      if (target?.src) parts.push(`src=${target.src.slice(0, 120)}`);
      else if (target?.href) parts.push(`href=${target.href.slice(0, 120)}`);

      const anyEvent = reason as unknown as { detail?: unknown; message?: unknown };
      if (typeof anyEvent.message === 'string' && anyEvent.message && !parts.some(p => p.startsWith('message='))) {
        parts.push(`message=${anyEvent.message.slice(0, 120)}`);
      }
      if (anyEvent.detail !== undefined) {
        try { parts.push(`detail=${JSON.stringify(anyEvent.detail).slice(0, 120)}`); } catch { /* noop */ }
      }

      return `Unhandled promise rejection (${ctor ?? 'Event'}): ${parts.join(' ')}`;
    }

    if (typeof DOMException !== 'undefined' && reason instanceof DOMException) {
      return `Unhandled promise rejection (DOMException ${reason.name}): ${reason.message}`;
    }

    if (reason && typeof reason === 'object') {
      let detail: string;
      try { detail = JSON.stringify(reason); } catch { detail = Object.prototype.toString.call(reason); }
      if (detail === '{}') {
        // Object with no enumerable keys — surface a few common diagnostic fields.
        const r = reason as Record<string, unknown>;
        const fields = ['name', 'message', 'code', 'status'].filter((k) => k in r);
        if (fields.length) detail = fields.map((k) => `${k}=${String(r[k]).slice(0, 80)}`).join(' ');
      }
      return `Unhandled promise rejection (${ctor ?? 'object'}): ${detail.slice(0, 200)}`;
    }

    return `Unhandled promise rejection (${typeof reason}): ${String(reason)}`;
  } catch {
    return 'Unhandled promise rejection';
  }
}

class ErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false, isBrowserExtensionError: false, retryCount: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    if (isBrowserExtensionDomError(error)) {
      return { hasError: true, isBrowserExtensionError: true };
    }
    return { hasError: true, isBrowserExtensionError: false };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // For browser extension DOM errors, auto-retry up to MAX_AUTO_RETRIES times
    if (isBrowserExtensionDomError(error) && this.state.retryCount < MAX_AUTO_RETRIES) {
      this.setState((prev) => ({
        hasError: false,
        isBrowserExtensionError: false,
        retryCount: prev.retryCount + 1,
      }));
      return;
    }

    logError(error, 'error_boundary');
    console.error('[ErrorBoundary] Caught render error:', error, info.componentStack);
  }

  // This boundary sits in the ROOT layout, outside every page, and the Sidebar is outside it — so a
  // <Link> navigation changes only `children`. Without this, one caught error left "Something went
  // wrong" on screen for every later page until a full reload (found triaging #422, 2026-09-26).
  // `prevState.hasError` too: when the NEW route's own render throws, the error and the key change land in
  // the SAME commit — resetting then would only re-render the page that just threw.
  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.state.hasError && prevState.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, isBrowserExtensionError: false, retryCount: 0 });
    }
  }

  componentDidMount() {
    window.addEventListener('error', this.handleWindowError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleWindowError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  handleWindowError = (event: ErrorEvent) => {
    // Skip errors from browser extensions
    if (event.filename?.includes('extension://')) return;

    // Skip cross-origin script errors (no message, no error object)
    if (!event.message && !event.error) return;

    const msg = event.message || '';
    if (isDomManipulationError(msg)) return;

    // Legacy-browser SyntaxError / ReferenceError — the legacy-browser-banner
    // script in app/layout.tsx already shows the user a banner for these.
    if (isLegacyBrowserSyntaxError(event.error, msg, event.filename)) return;

    if (isHydrationMismatchError(event.error, msg)) return;

    // The page is running through a URL-rewriting web proxy — its errors are the proxy's, not ours.
    if (isProxiedSessionError(event.error, event.filename)) return;

    const error = event.error instanceof Error
      ? event.error
      : syntheticError(
        event.message || 'Unknown window error',
        `window error, error was ${event.error === null ? 'null' : typeof event.error}`,
        event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined,
      );

    logError(error, 'window_error');
  };

  handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    // Resource-load failures (img/script/link onerror -> reject) surface here as
    // an error Event whose target is the failed element. These are asset issues,
    // not actionable JS bugs — the renderers fall back on a missing texture — so
    // they're noise in error_logs. (Assets verified present; the historical hits
    // were www-host loads from before the apex canonicalization.)
    if (isResourceLoadRejection(reason)) return;
    // Permissions-policy / Privacy-Sandbox denial from the global gtag script —
    // third-party noise, not an actionable app error.
    if (isPermissionPolicyDenial(reason)) return;
    if (reason instanceof Error) {
      logError(reason, 'unhandled_rejection');
      return;
    }
    if (typeof reason === 'string' && reason) {
      logError(syntheticError(reason, 'rejection reason was a string'), 'unhandled_rejection');
      return;
    }
    logError(
      syntheticError(describeRejectionReason(reason), `rejection reason was ${reason === null ? 'null' : typeof reason}`),
      'unhandled_rejection',
    );
  };

  handleReset = () => {
    this.setState({ hasError: false, isBrowserExtensionError: false, retryCount: 0 });
  };

  // A render that failed on the route's server payload (a body stream cut off mid-download — #422,
  // Firefox "Error in input stream") would re-throw from the router cache if only re-rendered. In ONE
  // transition, the reset commits together with the refreshed payload — a plain click setState would
  // commit first, against the old payload, and show the fallback again.
  handleRetry = () => {
    startTransition(() => {
      this.props.onRetry();
      this.setState({ hasError: false, isBrowserExtensionError: false, retryCount: 0 });
    });
  };

  render() {
    if (this.state.hasError) {
      // Browser extension error that exhausted auto-retries
      if (this.state.isBrowserExtensionError) {
        return (
          <div className="flex items-center justify-center min-h-[400px] p-6 max-lg:pt-[calc(3.75rem+1.5rem+env(safe-area-inset-top))]">
            <div className="bg-zinc-900 rounded-xl border border-amber-500/30 p-8 max-w-lg text-center">
              <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Browser extension conflict</h2>
              <p className="text-zinc-400 text-sm mb-4">
                A browser extension is modifying this page in a way that conflicts with how it renders.
                This is usually caused by ad blockers, translation tools, or extensions like Grammarly.
              </p>
              <p className="text-zinc-500 text-xs mb-6">
                Try disabling extensions for this site, or open the page in an incognito/private window.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={this.handleReset}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-emerald-950 rounded-lg transition-colors text-sm"
                >
                  Try Again
                </button>
                <a
                  href={typeof window !== 'undefined' ? window.location.href : '/'}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors text-sm"
                >
                  Reload Page
                </a>
              </div>
            </div>
          </div>
        );
      }

      // Generic error fallback
      return (
        <div className="flex items-center justify-center min-h-[400px] p-6 max-lg:pt-[calc(3.75rem+1.5rem+env(safe-area-inset-top))]">
          <div className="bg-zinc-900 rounded-xl border border-red-500/30 p-8 max-w-md text-center">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Something went wrong</h2>
            <p className="text-zinc-400 text-sm mb-6">
              An unexpected error occurred. The error has been logged automatically.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-emerald-950 rounded-lg transition-colors text-sm"
              >
                Try Again
              </button>
              <a
                href="/"
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors text-sm"
              >
                Go Home
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/** Root-layout error boundary: resets on navigation, and Try Again refetches the route. */
export default function ErrorBoundary({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  return (
    <ErrorBoundaryInner resetKey={pathname ?? ''} onRetry={() => router.refresh()}>
      {children}
    </ErrorBoundaryInner>
  );
}
